import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { State } from 'store/types';
import { AB_SIZE_MINI_PRODUCT } from 'shared/constants/experiments';
import { getEligibleForVariant } from 'shared/helpers/experimentV2';
import ProductDetails from './ProductDetails';
import VariantList from './VariantList';
import StickyInfo from './StickyInfo';
import Tabs from 'shared/components/Tabs';
import { tabKeys, BEST_SELLERS } from './constant';
import { PrimaryCategories, Variant } from 'shared/store/product/types';
import SizeList from './SizeList';
import { checkIfIsMiniEnabled } from 'helpers/utils/product';

interface AllVariantsProps {
  variants: Variant[]; // TODO: add support for listing variants array type
  product: Variant;
  showBestSeller: boolean;
  children: React.ReactNode;
  changeVariant: Function;
  isSize?: boolean;
  primaryCategories?: PrimaryCategories;
}

const ListingWrapper = styled.div`
  box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.05);
  background-color: #fff;
  overflow: auto;
  flex: 1;
  margin-bottom: 86px;
`;

const ChildWrapper = styled.div`
  position: relative;
`;

const AllVariants = ({
  product,
  variants,
  showBestSeller,
  changeVariant,
  isSize,
  children,
  primaryCategories,
}: AllVariantsProps) => {
  const [activeTab, setTab] = useState(0);
  const isMiniProductEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_SIZE_MINI_PRODUCT, 'A')
  );
  const [isMiniEnabled, setIsMiniEnabled] = useState(false);
  const { name, packSize, media: images, childId } = product;
  let childIndex = variants.findIndex((variant) => childId === variant.childId);
  if (childIndex < 0) {
    childIndex = 0;
  }

  const { variantName, mrp, discount, offerPrice, quantity } =
    variants[childIndex] || {};
  const isVariantOutOfStock = quantity === 0;
  const variantLength = variants ? variants.length : 0;
  const customStyle = {
    marginBottom: isVariantOutOfStock ? 158 : 86,
  };

  const optimizedImageUrlEnabled = useSelector(
    (state: State) => state?.remoteConfig?.switches?.optimizedImageUrlEnabled
  );
  const productCategories = useSelector(
    (state: State) =>
      state?.remoteConfig?.configs?.categoryControls?.priceWithSize
  );
  useEffect(() => {
    if (productCategories && primaryCategories) {
      setIsMiniEnabled(
        checkIfIsMiniEnabled(
          productCategories,
          isMiniProductEnabled,
          primaryCategories
        )
      );
    } else {
      setIsMiniEnabled(isMiniProductEnabled);
    }
  }, [isMiniProductEnabled, primaryCategories, productCategories]);

  return (
    <>
      <ProductDetails
        isSize={isSize}
        title={name}
        packSize={packSize}
        images={images}
        optionsLength={variantLength}
      />
      <ListingWrapper style={customStyle}>
        {!isSize && showBestSeller && (
          <Tabs
            list={tabKeys}
            activeTab={activeTab}
            onTabClick={setTab}
          />
        )}
        {!isSize ? (
          <VariantList
            changeVariant={changeVariant}
            variants={variants || []}
            bestSellersOnly={activeTab === tabKeys[BEST_SELLERS]}
            selectedProductId={childId}
            optimizedImageUrlEnabled={optimizedImageUrlEnabled}
          />
        ) : (
          <SizeList
            changeVariant={changeVariant}
            variants={variants || []}
            selectedProductId={childId}
            isMiniEnabled={isMiniEnabled}
          />
        )}
      </ListingWrapper>

      <StickyInfo
        discount={discount}
        name={variantName}
        mrp={mrp}
        offerPrice={offerPrice}
      >
        <ChildWrapper>{children}</ChildWrapper>
      </StickyInfo>
    </>
  );
};

export default AllVariants;
